import React from 'react';
import styled from 'styled-components';

import { VerticalLayout } from './Layout';
import { Text, Title } from './Text';

const textValue = (value: React.ReactNode) => {
    if (value === undefined || value === null) {
        return '-';
    }
    if (typeof value === 'boolean') {
        return value ? 'Enabled' : 'Disabled';
    }
    return value;
};

export const DataGrid = ({
    values = [],
    title,
    placeholder,
    condensed,
    twoCol,
    labelMinWidth,
    valueMinWidth,
    alignment = 'initial',
}: {
    values?: { label: JSX.Element | string; value?: JSX.Element | string | number | boolean }[];
    title?: string;
    placeholder?: string;
    condensed?: boolean;
    twoCol?: boolean;
    labelMinWidth?: string;
    valueMinWidth?: string;
    alignment?: 'initial' | 'center' | 'start' | 'end' | 'stretch';
}) => (
    <VerticalLayout $gap={8}>
        {title && <Title content>{title}</Title>}
        {values.length > 0 ? (
            <Grid
                $condensed={condensed}
                $twoCol={twoCol}
                $labelMinWidth={labelMinWidth}
                $valueMinWidth={valueMinWidth}
                $alignment={alignment}
            >
                {values.map(({ label, value }, idx) => {
                    return (
                        <React.Fragment key={idx}>
                            {React.isValidElement(label) ? <div>{label}</div> : <Text>{label}</Text>}
                            {React.isValidElement(value) ? <div>{value}</div> : <Text>{textValue(value)}</Text>}
                        </React.Fragment>
                    );
                })}
            </Grid>
        ) : (
            <Text $themeColor="subtle" $italic>
                {placeholder}
            </Text>
        )}
    </VerticalLayout>
);

const Grid = styled.div<{
    $condensed?: boolean;
    $twoCol?: boolean;
    $labelMinWidth?: string;
    $valueMinWidth?: string;
    $alignment?: string;
}>`
    display: grid;
    grid-template-columns: repeat(
        ${({ $twoCol = false }) => ($twoCol ? 2 : 1)},
        minmax(${({ $labelMinWidth = '150px' }) => $labelMinWidth}, max-content)
            minmax(${({ $valueMinWidth = '200px' }) => $valueMinWidth}, max-content)
    );
    column-gap: 16px;
    row-gap: ${({ $condensed = false }) => ($condensed ? 2 : 8)}px;
    align-items: ${({ $alignment = 'initial' }) => $alignment};
`;
